/**
 * Trainer Rubric List
 *
 * Lists rubrics with status filtering and create action.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { RequestPanel, StatusBadge } from '../../components/RequestPanel';
import { Code } from '../../components/JsonViewer';
import type { ApiResult, Rubric, RubricListResponse } from '../../lib/types';

type RubricStatus = 'draft' | 'published' | 'deprecated';

export function TrainerRubricList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<RubricListResponse> | null>(null);

  const statusFilter = (searchParams.get('status') as RubricStatus | null) || undefined;

  const loadRubrics = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.listRubrics({ limit: 50, status: statusFilter });
    setResult(res);
    if (res.response.data) {
      setRubrics(res.response.data.items);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    loadRubrics();
  }, [loadRubrics]);

  const handleStatusFilter = (status: RubricStatus | '') => {
    if (status) {
      setSearchParams({ status });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Rubrics
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Define scoring criteria with dimensions and rules
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-tertiary" onClick={loadRubrics} disabled={loading}>
            <iconify-icon icon="lucide:refresh-cw" width="16" height="16"></iconify-icon>
            Refresh
          </button>
          <Link to="/console/trainer/rubrics/new" className="btn btn-primary">
            <iconify-icon icon="lucide:plus" width="16" height="16"></iconify-icon>
            Create Rubric
          </Link>
        </div>
      </div>

      {/* Status Filter */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 8 }}>
        <button
          className="btn"
          onClick={() => handleStatusFilter('')}
          style={{
            fontSize: 13,
            padding: '8px 16px',
            background: !statusFilter ? 'var(--slate-900)' : 'var(--slate-100)',
            color: !statusFilter ? 'white' : 'var(--slate-600)',
            borderRadius: 9999,
          }}
        >
          All
        </button>
        <button
          className="btn"
          onClick={() => handleStatusFilter('draft')}
          style={{
            fontSize: 13,
            padding: '8px 16px',
            background: statusFilter === 'draft' ? 'var(--slate-900)' : 'var(--slate-100)',
            color: statusFilter === 'draft' ? 'white' : 'var(--slate-600)',
            borderRadius: 9999,
          }}
        >
          Draft
        </button>
        <button
          className="btn"
          onClick={() => handleStatusFilter('published')}
          style={{
            fontSize: 13,
            padding: '8px 16px',
            background: statusFilter === 'published' ? 'var(--slate-900)' : 'var(--slate-100)',
            color: statusFilter === 'published' ? 'white' : 'var(--slate-600)',
            borderRadius: 9999,
          }}
        >
          Published
        </button>
        <button
          className="btn"
          onClick={() => handleStatusFilter('deprecated')}
          style={{
            fontSize: 13,
            padding: '8px 16px',
            background: statusFilter === 'deprecated' ? 'var(--slate-900)' : 'var(--slate-100)',
            color: statusFilter === 'deprecated' ? 'white' : 'var(--slate-600)',
            borderRadius: 9999,
          }}
        >
          Deprecated
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Version</th>
              <th>Status</th>
              <th>Passing Score</th>
              <th>Max Score</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rubrics.map((r) => (
              <tr
                key={`${r.id}-${r.version}`}
                className="clickable"
                onClick={() => navigate(`/console/trainer/rubrics/${r.id}`)}
              >
                <td>
                  <Code>{r.id.slice(0, 12)}...</Code>
                </td>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td>{r.version}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>{r.passingScore}</td>
                <td>{r.maxScore}</td>
                <td className="text-muted text-small">
                  {new Date(r.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {rubrics.length === 0 && !loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <iconify-icon icon="lucide:scale" width="32" height="32" style={{ color: 'var(--slate-300)' }}></iconify-icon>
                    <span style={{ color: 'var(--slate-400)' }}>No rubrics found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
